/**
 * Canonical hardcoded fallback values for the sizer.
 *
 * Active ONLY when the live sizing API fails (network error, 5xx, timeout
 * > 3s, rate-limit). When fallback fires, the result card surfaces a one-
 * line note "Showing reference values — request a procurement-grade quote
 * for live sizing." The methodology panel surfaces a parallel note about
 * reference methodology.
 *
 * Tuned to canonical workload sizes (100 / 250 / 500 / 1000 VMs).
 * Source: typical Medium-tier CyCloud deployments based on the public-API
 * sanitizer banding model (monthly $500 rounding, TCV $50K rounding,
 * ±15% comparison bands).
 */

import type { PublicSizeResponse } from './api'

export const FALLBACK_RESPONSE_100VMS: PublicSizeResponse = {
  recommendation: {
    sku: 'CY-ST-02',
    nodes: 4,
    configSummary: '4 nodes · 384 cores · 4.0 TB RAM · 102 TB raw',
    limitFactor: 'cpu',
  },
  pricing: {
    monthlyLow: 26_000,
    monthlyHigh: 40_000,
    currency: 'USD',
    termMonths: 36,
    tcvLow: 936_000,
    tcvHigh: 1_440_000,
  },
  comparison: {
    cyCloud: { tcvLow: 950_000, tcvHigh: 1_450_000 },
    awsGovCloud: { tcvLow: 2_650_000, tcvHigh: 3_600_000 },
    azureGov: { tcvLow: 2_600_000, tcvHigh: 3_500_000 },
    diyFederal: { tcvLow: 4_300_000, tcvHigh: 5_800_000 },
  },
  methodology: {
    discountRatePct: 5.0,
    refreshReserveAnnualPct: 20,
    perCategoryEscalation: { compute: 3.0, power: 3.5, labor: 3.0, colo: 3.0 },
    stigComplianceAnnual: 'included',
    fteAllocationBand: '7-11 FTE',
    hyperscalerComparison: {
      comparisonBasket: [
        'compute',
        'memory',
        'storage_iops',
        'egress_nat',
        'load_balancing',
        'kubernetes_platform',
        'monitoring_logging',
        'automation_iac',
        'ai_ml_platform',
        'migration_tooling',
        'lifecycle_management',
        'operations_fte',
      ],
      awsGovCloud: {
        representativeFamily: 'm6i / r6i general-purpose',
        region: 'us-gov-east-1',
        commitmentPosture: '3yr-ri-all-upfront',
      },
      azureGov: {
        representativeFamily: 'Dasv5 / Easv5 general-purpose',
        region: 'USGov Virginia',
        commitmentPosture: '3yr-ri-monthly',
      },
    },
    comparisonConstructedDate: '2026-05-12',
  },
  deliveryLeadTimeWeeks: 22,
  disclaimer:
    'Estimate only. Actual pricing depends on configuration, term length, and contract vehicle.',
  needsArchitectureReview: false,
}

export const FALLBACK_RESPONSE_250VMS: PublicSizeResponse = {
  ...FALLBACK_RESPONSE_100VMS,
  recommendation: {
    sku: 'CY-ST-02',
    nodes: 8,
    configSummary: '8 nodes · 768 cores · 8.0 TB RAM · 204 TB raw',
    limitFactor: 'cpu',
  },
  pricing: {
    monthlyLow: 52_000,
    monthlyHigh: 80_000,
    currency: 'USD',
    termMonths: 36,
    tcvLow: 1_872_000,
    tcvHigh: 2_880_000,
  },
  comparison: {
    cyCloud: { tcvLow: 1_900_000, tcvHigh: 2_900_000 },
    awsGovCloud: { tcvLow: 5_300_000, tcvHigh: 7_200_000 },
    azureGov: { tcvLow: 5_200_000, tcvHigh: 7_000_000 },
    diyFederal: { tcvLow: 7_500_000, tcvHigh: 10_000_000 },
  },
}

export const FALLBACK_RESPONSE_500VMS: PublicSizeResponse = {
  ...FALLBACK_RESPONSE_100VMS,
  recommendation: {
    sku: 'CY-ST-03',
    nodes: 8,
    configSummary: '8 nodes · 1,152 cores · 12.0 TB RAM · 306 TB raw',
    limitFactor: 'cpu',
  },
  pricing: {
    monthlyLow: 78_000,
    monthlyHigh: 120_000,
    currency: 'USD',
    termMonths: 36,
    tcvLow: 2_808_000,
    tcvHigh: 4_320_000,
  },
  comparison: {
    cyCloud: { tcvLow: 2_850_000, tcvHigh: 4_350_000 },
    awsGovCloud: { tcvLow: 8_000_000, tcvHigh: 10_800_000 },
    azureGov: { tcvLow: 7_800_000, tcvHigh: 10_500_000 },
    diyFederal: { tcvLow: 12_500_000, tcvHigh: 16_000_000 },
  },
  methodology: {
    ...FALLBACK_RESPONSE_100VMS.methodology,
    fteAllocationBand: '11-15 FTE',
  },
}

export const FALLBACK_RESPONSE_1000VMS: PublicSizeResponse = {
  ...FALLBACK_RESPONSE_100VMS,
  recommendation: {
    sku: 'CY-ST-03',
    nodes: 16,
    configSummary: '16 nodes · 2,304 cores · 24.0 TB RAM · 612 TB raw',
    limitFactor: 'cpu',
  },
  pricing: {
    monthlyLow: 156_000,
    monthlyHigh: 240_000,
    currency: 'USD',
    termMonths: 36,
    tcvLow: 5_616_000,
    tcvHigh: 8_640_000,
  },
  comparison: {
    cyCloud: { tcvLow: 5_700_000, tcvHigh: 8_700_000 },
    awsGovCloud: { tcvLow: 16_000_000, tcvHigh: 21_500_000 },
    azureGov: { tcvLow: 15_500_000, tcvHigh: 21_000_000 },
    diyFederal: { tcvLow: 24_000_000, tcvHigh: 32_000_000 },
  },
  methodology: {
    ...FALLBACK_RESPONSE_100VMS.methodology,
    fteAllocationBand: '11-15 FTE',
  },
}

export function pickFallbackByVmCount(vmCount: number): PublicSizeResponse {
  if (vmCount <= 150) return FALLBACK_RESPONSE_100VMS
  if (vmCount <= 350) return FALLBACK_RESPONSE_250VMS
  if (vmCount <= 700) return FALLBACK_RESPONSE_500VMS
  return FALLBACK_RESPONSE_1000VMS
}

export function pickFallbackBySpend(monthlySpend: number): PublicSizeResponse {
  if (monthlySpend < 50_000) return FALLBACK_RESPONSE_100VMS
  if (monthlySpend < 120_000) return FALLBACK_RESPONSE_250VMS
  if (monthlySpend < 250_000) return FALLBACK_RESPONSE_500VMS
  return FALLBACK_RESPONSE_1000VMS
}
